import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/context/AuthContext";
import Layout from "@/components/Layout";
import {
  ArrowLeft, AlertTriangle, CheckCircle, Edit2, Trash2, Save,
  X, Plus, FileText, Shield, TrendingUp, Info, Star, ChevronDown, ChevronUp, RefreshCw, Brain
} from "lucide-react";

const SEV_CONFIG = {
  Critical: { cls: "severity-critical", bar: "bg-red-500" },
  High: { cls: "severity-high", bar: "bg-orange-500" },
  Medium: { cls: "severity-medium", bar: "bg-amber-500" },
  Low: { cls: "severity-low", bar: "bg-green-500" },
};

const RISK_CLR = { Low: "text-green-600 bg-green-50", Moderate: "text-amber-600 bg-amber-50", High: "text-orange-600 bg-orange-50", Critical: "text-red-600 bg-red-50" };

function FindingCard({ finding, onUpdate, onDismiss, onApprove }) {
  const [editing, setEditing] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [form, setForm] = useState({ ...finding });
  const [saving, setSaving] = useState(false);
  const cfg = SEV_CONFIG[finding.category] || SEV_CONFIG.Low;

  const save = async () => {
    setSaving(true);
    await onUpdate(finding.id, form);
    setSaving(false);
    setEditing(false);
  };

  if (finding.dismissed) return null;

  return (
    <div className={`border rounded-lg overflow-hidden ${finding.approved ? "border-green-200" : "border-slate-200"} animate-slide-up`} data-testid={`finding-${finding.id}`}>
      <div className={`flex items-start gap-3 p-4 ${finding.approved ? "bg-green-50/50" : "bg-white"}`}>
        <div className={`flex-shrink-0 px-2.5 py-0.5 rounded-full text-xs font-semibold border ${cfg.cls} mt-0.5`}>
          {finding.category}
        </div>
        <div className="flex-1 min-w-0">
          {editing ? (
            <input value={form.title} onChange={e => setForm({...form, title: e.target.value})}
              className="w-full h-8 px-2 border border-slate-200 rounded text-sm font-medium focus:outline-none focus:ring-2 focus:ring-navy/30 mb-1" />
          ) : (
            <h4 className="font-medium text-slate-800 text-sm">{finding.title}</h4>
          )}
          <div className="flex items-center gap-3 mt-1">
            <span className="text-xs text-slate-400">Confidence: <span className="font-medium text-slate-600">{Math.round((finding.ai_confidence || 0) * 100)}%</span></span>
            {finding.ai_generated && <span className="text-xs text-blue-500 flex items-center gap-1"><Shield className="w-3 h-3" />AI Generated</span>}
            {finding.library_ref && <span className="text-xs text-teal-600 flex items-center gap-1" data-testid={`library-ref-${finding.id}`}><Star className="w-3 h-3" />Carrier Standard</span>}
            {finding.edited_by && <span className="text-xs text-purple-500">Edited by {finding.edited_by}</span>}
            {finding.approved && <span className="text-xs text-green-600 flex items-center gap-1"><CheckCircle className="w-3 h-3" />Approved</span>}
          </div>
        </div>
        <div className="flex items-center gap-1.5 flex-shrink-0">
          <button onClick={() => setExpanded(!expanded)} className="p-1.5 text-slate-400 hover:text-slate-600 transition-colors" data-testid={`expand-${finding.id}`}>
            {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>
          {!editing && (
            <button onClick={() => { setEditing(true); setExpanded(true); }} className="p-1.5 text-slate-400 hover:text-navy transition-colors" data-testid={`edit-${finding.id}`}>
              <Edit2 className="w-3.5 h-3.5" />
            </button>
          )}
          {!finding.approved && (
            <button onClick={() => onApprove(finding.id)} className="p-1.5 text-green-500 hover:text-green-700 transition-colors" data-testid={`approve-${finding.id}`} title="Approve finding">
              <CheckCircle className="w-4 h-4" />
            </button>
          )}
          <button onClick={() => onDismiss(finding.id)} className="p-1.5 text-slate-400 hover:text-red-500 transition-colors" data-testid={`dismiss-${finding.id}`}>
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      {expanded && (
        <div className="border-t border-slate-100 p-4 bg-slate-50 space-y-3">
          <div>
            <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1 block">Description</label>
            {editing ? (
              <textarea value={form.description} onChange={e => setForm({...form, description: e.target.value})} rows={3}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-navy/30 resize-none" />
            ) : (
              <p className="text-sm text-slate-700">{finding.description}</p>
            )}
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1 block">Recommendation</label>
            {editing ? (
              <textarea value={form.recommendation} onChange={e => setForm({...form, recommendation: e.target.value})} rows={2}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-navy/30 resize-none" />
            ) : (
              <p className="text-sm text-slate-600">{finding.recommendation}</p>
            )}
          </div>
          {finding.reasoning && (
            <div className="flex items-start gap-2 bg-blue-50 border border-blue-100 rounded-lg p-2.5">
              <Info className="w-3.5 h-3.5 text-blue-500 mt-0.5 flex-shrink-0" />
              <p className="text-xs text-blue-700">{finding.reasoning}</p>
            </div>
          )}
          {editing && (
            <div className="flex items-center gap-2">
              <label className="text-xs font-medium text-slate-600">Severity:</label>
              <select value={form.category} onChange={e => setForm({...form, category: e.target.value})}
                className="h-7 px-2 border border-slate-200 rounded text-xs focus:outline-none bg-white">
                {["Critical","High","Medium","Low"].map(s => <option key={s}>{s}</option>)}
              </select>
            </div>
          )}
          {editing && (
            <div className="flex gap-2">
              <button onClick={save} disabled={saving}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-navy text-white rounded-lg text-xs font-medium hover:bg-navy-dark transition-colors disabled:opacity-60" data-testid={`save-finding-${finding.id}`}>
                <Save className="w-3 h-3" /> {saving ? "Saving..." : "Save Changes"}
              </button>
              <button onClick={() => { setEditing(false); setForm({...finding}); }}
                className="flex items-center gap-1.5 px-3 py-1.5 border border-slate-200 rounded-lg text-xs font-medium text-slate-600 hover:bg-slate-100 transition-colors">
                <X className="w-3 h-3" /> Cancel
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function ReviewInspection() {
  const { inspectionId } = useParams();
  const navigate = useNavigate();
  const [inspection, setInspection] = useState(null);
  const [findings, setFindings] = useState([]);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [finalizing, setFinalizing] = useState(false);
  const [activeTab, setActiveTab] = useState("findings");
  const [addingFinding, setAddingFinding] = useState(false);
  const [newFinding, setNewFinding] = useState({ category: "Medium", title: "", description: "", recommendation: "", reasoning: "" });

  const [trainingToast, setTrainingToast] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [inspRes, findingsRes] = await Promise.all([
        api.get(`/inspections/${inspectionId}`),
        api.get(`/inspections/${inspectionId}/findings`),
      ]);
      setInspection(inspRes.data);
      setFindings(findingsRes.data);
      try {
        const rRes = await api.get(`/reports/${inspectionId}`);
        setReport(rRes.data);
      } catch {}
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [inspectionId]);

  const handleUpdate = async (findingId, data) => {
    await api.put(`/inspections/${inspectionId}/findings/${findingId}`, data);
    setFindings(prev => prev.map(f => f.id === findingId ? { ...f, ...data } : f));
  };

  const handleDismiss = async (findingId) => {
    await api.put(`/inspections/${inspectionId}/findings/${findingId}`, { dismissed: true });
    setFindings(prev => prev.map(f => f.id === findingId ? { ...f, dismissed: true } : f));
  };

  const handleApprove = async (findingId) => {
    await api.put(`/inspections/${inspectionId}/findings/${findingId}`, { approved: true });
    setFindings(prev => prev.map(f => f.id === findingId ? { ...f, approved: true } : f));
    setTrainingToast(true);
    setTimeout(() => setTrainingToast(false), 3500);
  };

  const handleAddFinding = async () => {
    const res = await api.post(`/inspections/${inspectionId}/findings`, newFinding);
    setFindings(prev => [...prev, res.data]);
    setAddingFinding(false);
    setNewFinding({ category: "Medium", title: "", description: "", recommendation: "", reasoning: "" });
  };

  const handleFinalize = async () => {
    setFinalizing(true);
    try {
      await api.post(`/reports/${inspectionId}/finalize`, {});
      navigate(`/dashboard/report/${inspectionId}`);
    } catch (e) { console.error(e); }
    finally { setFinalizing(false); }
  };

  if (loading) return <Layout><div className="flex items-center justify-center h-64"><RefreshCw className="w-6 h-6 animate-spin text-navy" /></div></Layout>;

  const active = findings.filter(f => !f.dismissed);
  const critical = active.filter(f => f.category === "Critical");
  const high = active.filter(f => f.category === "High");
  const unapprovedCriticalHigh = [...critical, ...high].filter(f => !f.approved);
  const canFinalize = unapprovedCriticalHigh.length === 0 && active.length > 0;

  return (
    <Layout>
      {/* AI Training Toast */}
      {trainingToast && (
        <div className="fixed bottom-6 right-6 z-50 flex items-center gap-3 bg-slate-800 text-white px-4 py-3 rounded-xl shadow-xl animate-slide-up" data-testid="training-toast">
          <div className="w-7 h-7 bg-navy rounded-lg flex items-center justify-center flex-shrink-0">
            <Brain className="w-4 h-4 text-blue-300" />
          </div>
          <div>
            <div className="text-sm font-semibold">Added to AI training</div>
            <div className="text-xs text-slate-400">This finding will calibrate future surveys</div>
          </div>
        </div>
      )}
      <div className="space-y-6 animate-fade-in max-w-5xl">
        {/* Header */}
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-4">
            <button onClick={() => navigate("/dashboard")} className="text-slate-500 hover:text-navy transition-colors mt-1"><ArrowLeft className="w-5 h-5" /></button>
            <div>
              <h1 className="font-heading text-xl font-bold text-slate-800">{inspection?.insured_name}</h1>
              <p className="text-slate-500 text-sm">{inspection?.property_address} — {inspection?.inspection_type?.replace("_", " ")}</p>
              {/* Property Data badges */}
              {inspection?.property_data && (
                <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                  {inspection.property_data.flood_zone?.available && (
                    <span data-testid="flood-zone-badge"
                      className={`inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full ${
                        inspection.property_data.flood_zone.is_sfha
                          ? "bg-red-100 text-red-700 border border-red-200"
                          : "bg-green-100 text-green-700 border border-green-200"
                      }`}>
                      Flood Zone {inspection.property_data.flood_zone.zone_code}
                      {inspection.property_data.flood_zone.is_sfha ? " — High Risk" : " — Minimal Risk"}
                    </span>
                  )}
                  {inspection.property_data.property?.available && (
                    <>
                      {inspection.property_data.property.year_built && (
                        <span className="inline-flex items-center gap-1 text-xs text-slate-500 bg-slate-100 px-2 py-0.5 rounded-full border border-slate-200">
                          Built {inspection.property_data.property.year_built}
                        </span>
                      )}
                      {inspection.property_data.property.building_sqft && (
                        <span className="inline-flex items-center gap-1 text-xs text-slate-500 bg-slate-100 px-2 py-0.5 rounded-full border border-slate-200">
                          {inspection.property_data.property.building_sqft.toLocaleString()} sq ft
                        </span>
                      )}
                      {inspection.property_data.property.construction_type && (
                        <span className="inline-flex items-center gap-1 text-xs text-slate-500 bg-slate-100 px-2 py-0.5 rounded-full border border-slate-200">
                          {inspection.property_data.property.construction_type}
                        </span>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {report && (
              <button onClick={() => navigate(`/dashboard/report/${inspectionId}`)}
                className="flex items-center gap-2 px-3 py-2 border border-slate-200 rounded-lg text-sm font-medium text-slate-600 hover:bg-slate-50 transition-colors" data-testid="view-report-btn">
                <FileText className="w-4 h-4" /> View Report
              </button>
            )}
            <button
              data-testid="finalize-btn"
              onClick={handleFinalize} disabled={!canFinalize || finalizing}
              className="flex items-center gap-2 px-4 py-2 bg-navy text-white rounded-lg text-sm font-medium hover:bg-navy-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              title={!canFinalize ? "Approve all Critical/High findings before finalizing" : ""}
            >
              <CheckCircle className="w-4 h-4" />
              {finalizing ? "Finalizing..." : "Finalize & Generate Report"}
            </button>
          </div>
        </div>

        {/* Risk banner */}
        {report && (
          <div className={`flex items-center gap-4 p-4 rounded-xl border ${RISK_CLR[report.risk_level] || RISK_CLR.Low} border-current/20`} data-testid="risk-banner">
            <div className="text-3xl font-heading font-bold">{report.risk_score}<span className="text-lg font-normal">/100</span></div>
            <div>
              <div className="font-semibold text-sm">Overall Risk Score</div>
              <div className="text-sm opacity-80">{report.risk_level} Risk — {active.length} active finding{active.length !== 1 ? "s" : ""}</div>
            </div>
            <div className="ml-auto grid grid-cols-4 gap-3 text-center">
              {[["Critical", critical.length, "text-red-600"], ["High", high.length, "text-orange-600"],
                ["Medium", active.filter(f=>f.category==="Medium").length, "text-amber-600"],
                ["Low", active.filter(f=>f.category==="Low").length, "text-green-600"]].map(([lbl, cnt, cls]) => (
                <div key={lbl}>
                  <div className={`font-heading font-bold text-xl ${cls}`}>{cnt}</div>
                  <div className="text-xs opacity-70">{lbl}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Eligibility Assessment */}
        {report?.eligibility && (
          <div className="bg-white border border-slate-200 rounded-xl p-5" data-testid="eligibility-panel">
            <div className="flex items-center justify-between mb-3">
              <div>
                <h3 className="font-heading font-semibold text-slate-800 text-sm">
                  Eligibility Assessment — {report.eligibility.profile_name}
                </h3>
                {report.eligibility.summary && <p className="text-xs text-slate-500 mt-0.5">{report.eligibility.summary}</p>}
              </div>
              <span data-testid="eligibility-overall-badge" className={`px-3 py-1 rounded-full text-xs font-bold border ${
                report.eligibility.overall === "eligible" ? "bg-green-50 text-green-700 border-green-200"
                : report.eligibility.overall === "ineligible" ? "bg-red-50 text-red-700 border-red-200"
                : "bg-amber-50 text-amber-700 border-amber-200"}`}>
                {report.eligibility.overall === "eligible" ? "ELIGIBLE" : report.eligibility.overall === "ineligible" ? "INELIGIBLE" : "NEEDS REVIEW"}
              </span>
            </div>
            <ul className="space-y-2">
              {(report.eligibility.results || []).map((r, i) => (
                <li key={i} className="flex items-start gap-2.5 text-sm" data-testid={`eligibility-result-${i}`}>
                  <span className={`mt-1.5 w-2 h-2 rounded-full flex-shrink-0 ${
                    r.status === "meets" ? "bg-green-500" : r.status === "does_not_meet" ? "bg-red-500" : "bg-slate-300"}`} />
                  <div>
                    <span className="text-slate-700">{r.criterion}</span>
                    <span className={`ml-2 text-xs font-semibold ${
                      r.status === "meets" ? "text-green-600" : r.status === "does_not_meet" ? "text-red-600" : "text-slate-400"}`}>
                      {r.status === "meets" ? "Meets" : r.status === "does_not_meet" ? "Does not meet" : "Undetermined"}
                    </span>
                    {r.severity === "preferred" && <span className="ml-1.5 text-[10px] px-1.5 py-0.5 bg-slate-100 rounded-full text-slate-500">preferred</span>}
                    {r.note && <p className="text-xs text-slate-400 mt-0.5">{r.note}</p>}
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}

        {unapprovedCriticalHigh.length > 0 && (
          <div className="flex items-center gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg text-amber-700 text-sm" data-testid="approval-warning">
            <AlertTriangle className="w-4 h-4 flex-shrink-0" />
            <strong>Mandatory Review Gate:</strong> {unapprovedCriticalHigh.length} Critical/High finding{unapprovedCriticalHigh.length !== 1 ? "s" : ""} require your approval before the report can be finalized.
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-1 bg-slate-100 p-1 rounded-lg w-fit">
          {[["findings", `Findings (${active.length})`], ["summary", "Executive Summary"], ["messages", "Inspection Transcript"]].map(([k, label]) => (
            <button key={k} data-testid={`review-tab-${k}`} onClick={() => setActiveTab(k)}
              className={`px-4 py-1.5 rounded-md text-sm font-medium transition-colors duration-150 ${activeTab === k ? "bg-white text-navy shadow-sm" : "text-slate-500 hover:text-slate-700"}`}>
              {label}
            </button>
          ))}
        </div>

        {activeTab === "findings" && (
          <div className="space-y-3">
            {["Critical", "High", "Medium", "Low"].map(sev => {
              const sevFindings = active.filter(f => f.category === sev);
              if (!sevFindings.length) return null;
              return (
                <div key={sev}>
                  <h3 className={`text-xs font-semibold uppercase tracking-wider mb-2 ${SEV_CONFIG[sev].cls.split(" ")[0]}`}>{sev} Severity ({sevFindings.length})</h3>
                  <div className="space-y-2">
                    {sevFindings.map(f => <FindingCard key={f.id} finding={f} onUpdate={handleUpdate} onDismiss={handleDismiss} onApprove={handleApprove} />)}
                  </div>
                </div>
              );
            })}

            {active.length === 0 && (
              <div className="py-12 text-center text-slate-400">
                <CheckCircle className="w-10 h-10 mx-auto mb-3 text-green-400" />
                <p className="font-medium">No active findings</p>
              </div>
            )}

            <button onClick={() => setAddingFinding(!addingFinding)} data-testid="add-finding-btn"
              className="flex items-center gap-2 text-sm text-navy hover:text-navy-dark font-medium transition-colors">
              <Plus className="w-4 h-4" /> Add Manual Finding
            </button>

            {addingFinding && (
              <div className="border border-navy/20 bg-navy/5 rounded-xl p-4 space-y-3 animate-slide-up">
                <h4 className="font-medium text-slate-700 text-sm">Add Manual Finding</h4>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-medium text-slate-500 mb-1 block">Severity</label>
                    <select value={newFinding.category} onChange={e => setNewFinding({...newFinding, category: e.target.value})}
                      className="h-8 px-2 w-full border border-slate-200 rounded text-sm bg-white focus:outline-none focus:ring-2 focus:ring-navy/30">
                      {["Critical","High","Medium","Low"].map(s => <option key={s}>{s}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-slate-500 mb-1 block">Title *</label>
                    <input value={newFinding.title} onChange={e => setNewFinding({...newFinding, title: e.target.value})}
                      placeholder="Finding title" className="h-8 px-2 w-full border border-slate-200 rounded text-sm focus:outline-none focus:ring-2 focus:ring-navy/30" />
                  </div>
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-500 mb-1 block">Description</label>
                  <textarea value={newFinding.description} onChange={e => setNewFinding({...newFinding, description: e.target.value})} rows={2}
                    className="w-full px-3 py-2 border border-slate-200 rounded text-sm focus:outline-none focus:ring-2 focus:ring-navy/30 resize-none" />
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-500 mb-1 block">Recommendation</label>
                  <textarea value={newFinding.recommendation} onChange={e => setNewFinding({...newFinding, recommendation: e.target.value})} rows={2}
                    className="w-full px-3 py-2 border border-slate-200 rounded text-sm focus:outline-none focus:ring-2 focus:ring-navy/30 resize-none" />
                </div>
                <div className="flex gap-2">
                  <button onClick={handleAddFinding} className="px-4 py-1.5 bg-navy text-white rounded-lg text-sm font-medium hover:bg-navy-dark transition-colors" data-testid="save-new-finding-btn">Add Finding</button>
                  <button onClick={() => setAddingFinding(false)} className="px-4 py-1.5 border border-slate-200 rounded-lg text-sm font-medium text-slate-600 hover:bg-slate-50 transition-colors">Cancel</button>
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === "summary" && report && (
          <div className="bg-white border border-slate-200 rounded-xl p-6">
            <h3 className="font-heading font-semibold text-slate-700 mb-4">Executive Summary</h3>
            <p className="text-slate-700 leading-relaxed whitespace-pre-line">{report.executive_summary}</p>
            {report.project_plan?.length > 0 && (
              <div className="mt-6">
                <h3 className="font-heading font-semibold text-slate-700 mb-3">Project Plan</h3>
                <div className="space-y-2">
                  {report.project_plan.map((item, i) => (
                    <div key={i} className={`flex items-start gap-3 p-3 rounded-lg border ${SEV_CONFIG[item.priority]?.cls || "border-slate-200 bg-slate-50"}`}>
                      <span className="text-xs font-bold mt-0.5">{i + 1}</span>
                      <div className="flex-1">
                        <p className="text-sm font-medium text-slate-700">{item.action}</p>
                        <p className="text-xs text-slate-500 mt-0.5">{item.due_timeframe} — {item.responsible_party}</p>
                      </div>
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${SEV_CONFIG[item.priority]?.cls || "severity-low"}`}>{item.priority}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === "messages" && <InspectionTranscript inspectionId={inspectionId} />}
      </div>
    </Layout>
  );
}

function InspectionTranscript({ inspectionId }) {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/inspections/${inspectionId}/messages`)
      .then(r => setMessages(r.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [inspectionId]);

  if (loading) return <div className="py-8 text-center"><RefreshCw className="w-5 h-5 animate-spin text-navy mx-auto" /></div>;

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-6 space-y-4 max-h-[600px] overflow-y-auto">
      {messages.map(m => (
        <div key={m.id} className={`flex ${m.role === "assistant" ? "justify-start" : "justify-end"}`}>
          <div className={`max-w-lg px-4 py-3 rounded-xl text-sm leading-relaxed ${m.role === "assistant" ? "chat-bubble-ai" : "chat-bubble-user"}`}>
            <div className="font-medium text-xs mb-1 opacity-70">{m.role === "assistant" ? "AI Inspector" : "Insured"}</div>
            <p className="whitespace-pre-line">{m.content}</p>
            {m.photo_urls?.length > 0 && (
              <div className="mt-2 flex gap-2">
                {m.photo_urls.map((url, i) => (
                  <img key={i} src={`${process.env.REACT_APP_BACKEND_URL}${url}`} alt="uploaded" className="h-16 w-16 object-cover rounded-md" />
                ))}
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}
